import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { can, WINE_COLORS, type WineColor } from '@gwc/shared';
import { winesApi } from '../api/wines';
import type { CatalogWineListItem } from '../api/models';
import { useAuth } from '../auth/AuthProvider';
import { WineFormDialog } from '../components/WineFormDialog';
import { WineColorChip } from '../components/WineColorChip';
import { Pagination } from '../components/Pagination';
import { CardGridSkeleton } from '../components/Skeleton';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

const PAGE_SIZE = 24;

export function Cellar() {
  const { user } = useAuth();
  const canEdit = user ? can(user.roles, 'records:contribute') : false;
  const [q, setQ] = useState('');
  const [color, setColor] = useState<WineColor | ''>('');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const debouncedQ = useDebouncedValue(q);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['wines', { q: debouncedQ, color, page }],
    queryFn: () =>
      winesApi.search({
        q: debouncedQ || undefined,
        color: color || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
  });
  const items = data?.items ?? [];

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold-400">The Cellar</p>
          <h1 className="mt-2 font-display text-3xl text-cream-50">Wines</h1>
          <p className="mt-1 text-sm text-cream-300/60">
            Every wine the club has poured — add one once, and it links to every tasting it appears in.
          </p>
        </div>
        {canEdit && (
          <button type="button" className="btn-primary" onClick={() => setAdding(true)}>
            + Add a wine
          </button>
        )}
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <input
          className="field max-w-xs"
          placeholder="Search name, producer, region…"
          aria-label="Search cellar"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="field w-auto capitalize"
          aria-label="Filter by colour"
          value={color}
          onChange={(e) => {
            setColor(e.target.value as WineColor | '');
            setPage(1);
          }}
        >
          <option value="">All colours</option>
          {WINE_COLORS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <span className="self-center text-sm text-cream-300/50">
          {data ? `${data.total} wine${data.total === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {isLoading ? (
        <CardGridSkeleton />
      ) : isError ? (
        <p className="text-wine-300">Could not load the Cellar. Try again.</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-cream-300/50">
          No wines yet.{canEdit ? ' Add the first one to start building the Cellar.' : ''}
        </p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((w) => (
              <WineCard key={w.id} wine={w} />
            ))}
          </div>
          {data && (
            <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onPage={setPage} />
          )}
        </>
      )}

      {adding && (
        <WineFormDialog editing={null} onClose={() => setAdding(false)} onSaved={() => setAdding(false)} />
      )}
    </div>
  );
}

function WineCard({ wine }: { wine: CatalogWineListItem }) {
  const grapes = wine.varietals.join(', ');
  const place = [wine.region, wine.country].filter(Boolean).join(' · ');
  return (
    <Link to={`/cellar/${wine.id}`} className="card flex gap-3 p-4 transition hover:border-gold-600/40">
      {wine.labelImageUrl ? (
        <img
          src={wine.labelImageUrl}
          alt=""
          className="h-16 w-12 shrink-0 rounded border border-ink-800 object-cover"
          onError={(e) => ((e.target as HTMLImageElement).style.visibility = 'hidden')}
        />
      ) : (
        <div className="grid h-16 w-12 shrink-0 place-items-center rounded border border-ink-800 bg-ink-950 font-display text-lg text-gold-400/60">
          {(wine.wineName ?? '?').charAt(0)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-cream-50">
            {wine.vintage ? `${wine.vintage} ` : ''}
            {wine.wineName ?? 'Unnamed wine'}
          </span>
          <WineColorChip color={wine.color} />
        </div>
        <div className="mt-0.5 truncate text-xs text-cream-300/60">{wine.producer ?? 'Unknown producer'}</div>
        {(grapes || place) && (
          <div className="mt-0.5 truncate text-xs text-cream-300/40">{[grapes, place].filter(Boolean).join(' — ')}</div>
        )}
        <div className="mt-1.5 flex gap-3 text-[11px] text-cream-300/50">
          <span>
            Poured <span className="text-gold-300">{wine.timesPoured}×</span>
          </span>
          {wine.avgScore != null && (
            <span>
              Avg <span className="text-gold-300">{wine.avgScore.toFixed(1)}</span>
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
